import React from 'react';
import clsx from 'clsx';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import FaIconUvilo from '@erikdakoda/ui/components/FaIconUvilo';
import ScrollBox from '@erikdakoda/ui/components/ScrollBox';
import { getArtifactType } from '../shared/artifactRegistry';

export type ArtifactPreviewCardProps = {
  /** Artifact MIME type (e.g. `text/markdown`). */
  artifactType: string;
  /** Human-readable artifact title. */
  title: string;
  /** Highlights the card when the artifact is open in the workspace panel. */
  isActive?: boolean;
  /** Opens the artifact in the workspace panel when the card is clicked. */
  onClick?: () => void;
  /** Extra icon buttons rendered beside the title (copy, download, edit, display toggle, etc.). */
  headerActions?: React.ReactNode;
  /** Content below the divider (preview text, inline renderer, streaming state). */
  children?: React.ReactNode;
  /** Slate void nodes pass `contentEditable={false}` wrapper attributes. */
  attributes?: React.HTMLAttributes<HTMLDivElement>;
  className?: string;
};

/**
 * Compact artifact preview card with a type icon column and title/actions row.
 * Shared by chat tool output and markdown embeds.
 */
export default function ArtifactPreviewCard({
  artifactType,
  title,
  isActive = false,
  onClick,
  headerActions,
  children,
  attributes,
  className,
}: ArtifactPreviewCardProps) {
  'use memo';

  const definition = getArtifactType(artifactType);
  const typeLabel = definition?.name ?? artifactType;
  const typeIcon = definition?.icon ?? 'file';
  const clickable = Boolean(onClick);

  return (
    <Paper
      className={clsx('artifact-preview-card', className)}
      variant="outlined"
      onClick={onClick}
      {...attributes}
      sx={{
        my: 1,
        overflow: 'hidden',
        cursor: clickable ? 'pointer' : 'default',
        borderColor: isActive ? 'primary.main' : 'divider',
        '&:hover': clickable ? { bgcolor: 'action.hover' } : {},
        transition: 'border-color 0.15s, background-color 0.15s',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'min-content minmax(0, 1fr)',
          alignItems: 'stretch',
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            gridColumn: '1',
            paddingX: 2,
            paddingY: 1,
            borderRight: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <FaIconUvilo size="xl" icon={typeIcon} prefix="fat" />
          <Typography variant="caption" sx={{ textAlign: 'center', lineHeight: 1.2 }}>
            {typeLabel}
          </Typography>
        </Box>
        <Box
          sx={{
            gridColumn: '2',
            display: 'grid',
            gridTemplateRows: '2.5rem min-content',
            alignItems: 'stretch',
            minWidth: 0,
          }}
        >
          <Box>
            <ScrollBox overflowYHidden innerSx={{ height: '100%' }}>
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingX: 3,
                  gap: 2,
                }}
              >
                <Typography
                  variant="condensed5"
                  sx={{ marginTop: '2px', whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  {title}
                </Typography>
                {headerActions ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    {headerActions}
                  </Box>
                ) : null}
              </Box>
            </ScrollBox>
          </Box>

          {children ? (
            <Box
              sx={{
                minWidth: 0,
                overflow: 'hidden',
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              {children}
            </Box>
          ) : null}
        </Box>
      </Box>
    </Paper>
  );
}
